import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import Swal from 'sweetalert2';

const PageContainer = styled.div`
  padding: 24px;
  width: 100%;
  max-width: none;
  margin: 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
    border-color: #d1d5db;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #00d4aa;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #00b894;
    transform: translateY(-2px);
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const RulesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RuleCard = styled.div<{ $isActive: boolean }>`
  background: ${props => props.$isActive ? 'white' : '#f7fafc'};
  border: 2px solid ${props => props.$isActive ? '#e2e8f0' : '#cbd5e0'};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  opacity: ${props => props.$isActive ? 1 : 0.6};
  transition: all 0.2s;

  &:hover {
    border-color: #00d4aa;
    box-shadow: 0 4px 12px rgba(0, 212, 170, 0.15);
  }
`;

const RuleContent = styled.div`
  flex: 1;
`;

const RuleTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 8px;
`;

const RuleDescription = styled.p`
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
`;

const RuleActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 16px;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'danger' | 'secondary' }>`
  padding: 8px;
  background: ${props =>
    props.$variant === 'danger' ? '#ef4444' :
    props.$variant === 'secondary' ? '#6b7280' :
    '#00d4aa'
  };
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
    transform: translateY(-2px);
  }
`;

const Modal = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  background: ${props => props.$variant === 'secondary' ? '#6b7280' : '#00d4aa'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #64748b;
`;

interface Rule {
  id: number;
  title: string;
  description: string;
  order: number;
  is_active: boolean;
}

const CompanyRulesPage: React.FC = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    loadRules();
  }, []);

  const loadRules = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/company-rules`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setRules(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
      Swal.fire('Erro', 'Não foi possível carregar as regras', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingRule(null);
    setFormData({
      title: '',
      description: '',
      order: rules.length,
      is_active: true
    });
    setShowModal(true);
  };

  const handleEdit = (rule: Rule) => {
    setEditingRule(rule);
    setFormData({
      title: rule.title,
      description: rule.description,
      order: rule.order,
      is_active: rule.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const url = editingRule
        ? `${apiUrl}/company-rules/${editingRule.id}`
        : `${apiUrl}/company-rules`;
      const method = editingRule ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire('Sucesso!', data.message, 'success');
        setShowModal(false);
        loadRules();
      } else {
        Swal.fire('Erro', data.message, 'error');
      }
    } catch (error) {
      console.error('Erro ao salvar regra:', error);
      Swal.fire('Erro', 'Não foi possível salvar a regra', 'error');
    }
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: 'Confirmar exclusão',
      text: 'Tem certeza que deseja deletar esta regra?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sim, deletar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/company-rules/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire('Deletado!', data.message, 'success');
        loadRules();
      } else {
        Swal.fire('Erro', data.message, 'error');
      }
    } catch (error) {
      console.error('Erro ao deletar regra:', error);
      Swal.fire('Erro', 'Não foi possível deletar a regra', 'error');
    }
  };

  const handleToggleActive = async (rule: Rule) => {
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/company-rules/${rule.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...rule,
          is_active: !rule.is_active
        })
      });

      const data = await response.json();
      if (data.success) {
        loadRules();
      }
    } catch (error) {
      console.error('Erro ao atualizar regra:', error);
    }
  };

  if (loading) {
    return <PageContainer>Carregando...</PageContainer>;
  }

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} />
            Voltar
          </BackButton>
          <Title>Regras do Programa de Fidelidade</Title>
        </HeaderLeft>
        <AddButton onClick={handleAddNew}>
          <Plus size={20} />
          Nova Regra
        </AddButton>
      </Header>

      <Card>
        {rules.length === 0 ? (
          <EmptyState>
            <p>Nenhuma regra cadastrada ainda.</p>
            <p>Clique em "Nova Regra" para adicionar a primeira regra do seu programa.</p>
          </EmptyState>
        ) : (
          <RulesList>
            {rules.map(rule => (
              <RuleCard key={rule.id} $isActive={rule.is_active}>
                <RuleContent>
                  <RuleTitle>{rule.title}</RuleTitle>
                  <RuleDescription>{rule.description}</RuleDescription>
                </RuleContent>
                <RuleActions>
                  <ActionButton
                    $variant="secondary"
                    onClick={() => handleToggleActive(rule)}
                    title={rule.is_active ? 'Desativar' : 'Ativar'}
                  >
                    {rule.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                  </ActionButton>
                  <ActionButton onClick={() => handleEdit(rule)}>
                    <Edit2 size={16} />
                  </ActionButton>
                  <ActionButton $variant="danger" onClick={() => handleDelete(rule.id)}>
                    <Trash2 size={16} />
                  </ActionButton>
                </RuleActions>
              </RuleCard>
            ))}
          </RulesList>
        )}
      </Card>

      <Modal $show={showModal} onClick={() => setShowModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>{editingRule ? 'Editar Regra' : 'Nova Regra'}</ModalTitle>
          </ModalHeader>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Título da Regra *</Label>
              <Input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Ex: Como Acumular Pontos"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Descrição *</Label>
              <TextArea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descreva a regra em detalhes..."
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Ordem de Exibição</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                min="0"
              />
            </FormGroup>

            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              />
              <Label htmlFor="is_active">Regra ativa</Label>
            </CheckboxGroup>

            <ButtonGroup>
              <Button type="button" $variant="secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingRule ? 'Salvar Alterações' : 'Criar Regra'}
              </Button>
            </ButtonGroup>
          </Form>
        </ModalContent>
      </Modal>
    </PageContainer>
  );
};

export default CompanyRulesPage;
